import { Injectable } from '@angular/core';
import {
  getImportFileBusinessStatusName,
  getImportFileProcessingStatusName,
  ImportFileBusinessStatus,
  ImportFileProcessingStatus,
  RequestStatus,
} from '@models';
import {
  DisputeRequestItemStatus,
  DisputeRequestStatus,
  getDisputeRequestItemStatusName,
  getDisputeRequestStatusName,
} from '@models/dispute-requests.models';
import { ExpediteRequestStatus, getExpediteRequestStatusName } from '@models/expedite-requests.models';
import { getInformationRequestStatusName, InformationRequestStatus } from '@models/information-requests.models';
import {
  getItemClassificationTypeStatusClassName,
  getItemClassificationTypeStatusName,
  ItemClassificationCodeStatus,
  ItemStatus,
} from '@models/items.models';

@Injectable({
  providedIn: 'root',
})
export class ChipHelperService {
  static getItemStatusName(context: StatusContext, status: unknown): string {
    switch (context) {
      case 'item':
        return ItemStatusHelperService.getStatusName(status as ItemStatus);
      case 'item-classification-code':
        return ItemClassificationCodeStatusHelperService.getStatusName(status as ItemClassificationCodeStatus);
      case 'request':
        return RequestStatusHelperService.getStatusName(status as RequestStatus);
      case 'information-request':
        return InformationRequestStatusHelperService.getStatusName(status as InformationRequestStatus);
      case 'dispute-request':
        return DisputeRequestStatusHelperService.getStatusName(status as DisputeRequestStatus);
      case 'dispute-item':
        return DisputeItemStatusHelperService.getStatusName(status as DisputeRequestItemStatus);
      case 'expedite-request':
        return ExpediteRequestStatusHelperService.getStatusName(status as ExpediteRequestStatus);
      case 'import-file':
        return ImportFileBusinessStatusHelperService.getStatusName(status as ImportFileBusinessStatus);
      case 'import-file-processing':
        return ImportFileProcessingStatusHelperService.getStatusName(status as ImportFileProcessingStatus);
    }
  }

  static getItemStatusChipClassName(context: StatusContext, status: unknown): string {
    switch (context) {
      case 'item':
        return ItemStatusHelperService.getClassName(status as ItemStatus);
      case 'item-classification-code':
        return ItemClassificationCodeStatusHelperService.getClassName(status as ItemClassificationCodeStatus);
      case 'request':
        return RequestStatusHelperService.getClassName(status as RequestStatus);
      case 'information-request':
        return InformationRequestStatusHelperService.getClassName(status as InformationRequestStatus);
      case 'dispute-request':
        return DisputeRequestStatusHelperService.getClassName(status as DisputeRequestStatus);
      case 'dispute-item':
        return DisputeItemStatusHelperService.getClassName(status as DisputeRequestItemStatus);
      case 'expedite-request':
        return ExpediteRequestStatusHelperService.getClassName(status as ExpediteRequestStatus);
      case 'import-file':
        return ImportFileBusinessStatusHelperService.getClassName(status as ImportFileBusinessStatus);
      case 'import-file-processing':
        return ImportFileProcessingStatusHelperService.getClassName(status as ImportFileProcessingStatus);
    }
  }
}

export type StatusContext =
  | 'request'
  | 'item'
  | 'information-request'
  | 'item-classification-code'
  | 'dispute-request'
  | 'dispute-item'
  | 'expedite-request'
  | 'import-file'
  | 'import-file-processing';

class RequestStatusHelperService {
  static getStatusName(status: RequestStatus | null): string {
    switch (status) {
      case RequestStatus.Submitted:
        return 'item-status.submitted';
      case RequestStatus.InProgress:
        return 'item-status.in-progress';
      case RequestStatus.PendingInformation:
        return 'item-status.pending-information';
      case RequestStatus.Completed:
        return 'item-status.completed';
      case RequestStatus.Cancelled:
        return 'item-status.cancelled';
      case RequestStatus.Initiating:
        return 'request-status.initiating';
      default:
        return 'item-status.unknown';
    }
  }

  static getClassName(status: RequestStatus | null): string {
    switch (status) {
      case RequestStatus.Submitted:
      case RequestStatus.Initiating:
        return 'submitted';
      case RequestStatus.InProgress:
        return 'in-progress';
      case RequestStatus.PendingInformation:
        return 'pending-information';
      case RequestStatus.Completed:
        return 'completed';
      case RequestStatus.Cancelled:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}

class ItemStatusHelperService {
  static getStatusName(status: ItemStatus | null): string {
    switch (status) {
      case ItemStatus.Submitted:
        return 'item-status.submitted';
      case ItemStatus.InProgress:
        return 'item-status.in-progress';
      case ItemStatus.PendingInformation:
        return 'item-status.pending-information';
      case ItemStatus.Completed:
        return 'item-status.completed';
      case ItemStatus.Cancelled:
        return 'item-status.cancelled';
      default:
        return 'item-status.unknown';
    }
  }

  static getClassName(status: ItemStatus | null): string {
    switch (status) {
      case ItemStatus.Submitted:
        return 'submitted';
      case ItemStatus.InProgress:
        return 'in-progress';
      case ItemStatus.PendingInformation:
        return 'pending-information';
      case ItemStatus.Completed:
        return 'completed';
      case ItemStatus.Cancelled:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}

class ItemClassificationCodeStatusHelperService {
  static getStatusName(status: ItemClassificationCodeStatus | null): string {
    return getItemClassificationTypeStatusName(status);
  }

  static getClassName(status: ItemClassificationCodeStatus | null): string {
    return getItemClassificationTypeStatusClassName(status);
  }
}

class InformationRequestStatusHelperService {
  static getStatusName(status: InformationRequestStatus | null): string {
    return getInformationRequestStatusName(status);
  }

  static getClassName(status: InformationRequestStatus | null): string {
    switch (status) {
      case InformationRequestStatus.Open:
        return 'pending-information';
      case InformationRequestStatus.Resolved:
        return 'submitted';
      case InformationRequestStatus.Closed:
        return 'completed';
      default:
        return 'unknown';
    }
  }
}

class DisputeRequestStatusHelperService {
  static getStatusName(status: DisputeRequestStatus | null): string {
    return getDisputeRequestStatusName(status);
  }

  static getClassName(status: DisputeRequestStatus | null): string {
    switch (status) {
      case DisputeRequestStatus.Initiating:
        return 'submitted';
      case DisputeRequestStatus.Submitted:
        return 'submitted';
      case DisputeRequestStatus.InProgress:
        return 'in-progress';
      case DisputeRequestStatus.Completed:
        return 'completed';
      default:
        return 'unknown';
    }
  }
}

class DisputeItemStatusHelperService {
  static getStatusName(status: DisputeRequestItemStatus | null): string {
    return getDisputeRequestItemStatusName(status);
  }

  static getClassName(status: DisputeRequestItemStatus | null): string {
    switch (status) {
      case DisputeRequestItemStatus.Submitted:
        return 'submitted';
      case DisputeRequestItemStatus.InProgress:
        return 'in-progress';
      case DisputeRequestItemStatus.Agreed:
        return 'agreed';
      case DisputeRequestItemStatus.InReclassification:
        return 'in-reclassification';
      case DisputeRequestItemStatus.Disagreed:
        return 'disagreed';
      case DisputeRequestItemStatus.Reclassified:
        return 'reclassified';
      case DisputeRequestItemStatus.Cancelled:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}

class ExpediteRequestStatusHelperService {
  static getStatusName(status: ExpediteRequestStatus | null): string {
    return getExpediteRequestStatusName(status);
  }

  static getClassName(status: ExpediteRequestStatus | null): string {
    switch (status) {
      case ExpediteRequestStatus.Initiating:
        return 'initiating';
      case ExpediteRequestStatus.Submitted:
        return 'submitted';
      case ExpediteRequestStatus.InProgress:
        return 'in-progress';
      case ExpediteRequestStatus.Approved:
        return 'approved';
      case ExpediteRequestStatus.Rejected:
        return 'rejected';
      case ExpediteRequestStatus.Obsolete:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}

class ImportFileBusinessStatusHelperService {
  static getStatusName(status: ImportFileBusinessStatus | null): string {
    return getImportFileBusinessStatusName(status);
  }

  static getClassName(status: ImportFileBusinessStatus | null): string {
    switch (status) {
      case ImportFileBusinessStatus.None:
        return 'none';
      case ImportFileBusinessStatus.Submitted:
        return 'submitted';
      case ImportFileBusinessStatus.ApprovalRequested:
        return 'in-progress';
      case ImportFileBusinessStatus.Approved:
        return 'approved';
      case ImportFileBusinessStatus.Rejected:
        return 'rejected';
      default:
        return 'unknown';
    }
  }
}

class ImportFileProcessingStatusHelperService {
  static getStatusName(status: ImportFileProcessingStatus | null): string {
    return getImportFileProcessingStatusName(status);
  }

  static getClassName(status: ImportFileProcessingStatus | null): string {
    switch (status) {
      case ImportFileProcessingStatus.Uploaded:
        return 'submitted';
      case ImportFileProcessingStatus.ValidationInProgress:
      case ImportFileProcessingStatus.ApprovalStarted:
      case ImportFileProcessingStatus.ApprovalInProgress:
      case ImportFileProcessingStatus.EmissionsCalculationInProgress:
        return 'in-progress';
      case ImportFileProcessingStatus.ValidationFailed:
      case ImportFileProcessingStatus.ApprovalFailed:
      case ImportFileProcessingStatus.EmissionsCalculationFailed:
        return 'rejected';
      case ImportFileProcessingStatus.ValidationSucceeded:
      case ImportFileProcessingStatus.ApprovalSucceeded:
      case ImportFileProcessingStatus.EmissionsCalculationSucceeded:
        return 'approved';
      default:
        return 'unknown';
    }
  }
}
